import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { ATMOSPHERIC_IMAGES } from "@/lib/api";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`${t("auth.welcome_toast")}, ${user.full_name}`);
      const to = user.role === "guest" ? "/" : "/dashboard";
      navigate(location.state?.from || to);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.invalid_creds"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="login-page" className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={ATMOSPHERIC_IMAGES.detail} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute bottom-12 left-12 text-white max-w-md">
          <div className="overline text-accent mb-4">{t("auth.reserved_area")}</div>
          <h2 className="font-serif text-5xl leading-tight">{t("auth.welcome_back")}</h2>
          <p className="mt-4 text-white/80">{t("auth.reserved_sub")}</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-md">
          <Link to="/" className="overline text-accent">{t("auth.back_to_site")}</Link>
          <h1 className="font-serif text-4xl mt-6">{t("auth.login_title")}</h1>
          <p className="text-muted-foreground mt-2">{t("auth.login_sub")}</p>

          <div className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input data-testid="login-email" id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2" placeholder="admin@ladimoradipatty.it" />
            </div>
            <div>
              <Label htmlFor="pw">{t("auth.password")}</Label>
              <Input data-testid="login-password" id="pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2" />
            </div>
            <button
              data-testid="login-submit"
              disabled={loading}
              className="btn-shimmer w-full py-3.5 rounded-full bg-foreground text-background hover:bg-accent transition-colors disabled:opacity-50 text-sm tracking-wide"
            >
              {loading ? t("auth.loading") : t("auth.submit_login")}
            </button>
          </div>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">{t("auth.or")}</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <Link to="/register" className="block text-center py-3 rounded-full border border-border hover:border-accent transition-colors text-sm">
            {t("auth.create_account")}
          </Link>
        </form>
      </div>
    </main>
  );
};

export default LoginPage;
